import { ANON_IDENTITY, storeName } from '@jbroll/rowboat-client';
import { APP_STORE, IDENTIFY_QUEUE_DB, PHOTO_QUEUE_DB } from './storeNames';

// Schema versions 4 and 5 named every database for the version. Version 5's are carried to the
// stable names; older ones were already abandoned by the bump that followed them.
const CARRIED_APP = 'rosa-house-v5';
const SUPERSEDED = /^rosa-house(-v\d+)?(::|-photo-outbox$|-identify-outbox$)/;

/**
 * Names of the legacy databases whose copy finished. IndexedDB rather than localStorage: it is
 * site data stored and evicted with the copies themselves, so a marker never outlives its copy.
 */
export const CARRY_DB = 'rosa-house-carried';
const CARRY_STORE = 'done';

/** A stable name to carry `name` to, null to delete it, undefined when it is not a legacy database. */
export function legacyTarget(name: string): string | null | undefined {
  if (name === `${CARRIED_APP}-photo-outbox`) return PHOTO_QUEUE_DB;
  if (name === `${CARRIED_APP}-identify-outbox`) return IDENTIFY_QUEUE_DB;
  if (name.startsWith(`${CARRIED_APP}::`)) return APP_STORE + name.slice(CARRIED_APP.length);
  return SUPERSEDED.test(name) ? null : undefined;
}

type OnBlocked = (name: string) => void;

/** One copy transaction holds at most this many records or roughly this many bytes. */
export interface CarryBatch {
  records: number;
  bytes: number;
}
const BATCH: CarryBatch = { records: 50, bytes: 64 * 1024 * 1024 };

/**
 * Copies each version 5 database to its stable name in bounded batches, marks it carried, then
 * deletes it, and deletes every older legacy database. Run before anything opens a store. A target
 * without a marker is an interrupted copy: it is deleted and copied again.
 */
export async function carryLegacyStores(
  idb: IDBFactory = indexedDB,
  opts: { onBlocked?: OnBlocked; batch?: CarryBatch } = {},
): Promise<void> {
  // One tab at a time: a second tab that read the markers before the first wrote its own would
  // delete the first's finished copy as unfinished. Every browser Rosa supports has Web Locks.
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  const run = () => carryUnlocked(idb, opts.batch ?? BATCH, opts.onBlocked);
  if (locks) await locks.request(CARRY_LOCK, run);
  else await run();
}

const CARRY_LOCK = 'rosa-house-carry';

async function carryUnlocked(idb: IDBFactory, batch: CarryBatch, onBlocked?: OnBlocked) {
  const present = new Set((await idb.databases()).flatMap((d) => (d.name ? [d.name] : [])));
  const done = present.has(CARRY_DB) ? await carried(idb) : new Set<string>();
  for (const name of present) {
    const target = legacyTarget(name);
    if (target === undefined) continue;
    if (target !== null && !done.has(name)) {
      const stale = present.has(target);
      if (!(await copyDatabase(idb, name, target, stale, batch, onBlocked))) continue;
      await markCarried(idb, name);
    }
    await settle(idb.deleteDatabase(name), name, onBlocked);
  }
}

interface StoreShape {
  name: string;
  keyPath: string | string[] | null;
  autoIncrement: boolean;
  indexes: { name: string; keyPath: string | string[]; unique: boolean; multiEntry: boolean }[];
}

/**
 * Copies `from` to `to`, first deleting a `stale` unfinished `to`. False when `from` no longer
 * exists, which means another carry finished it; nothing is created then.
 */
async function copyDatabase(
  idb: IDBFactory,
  from: string,
  to: string,
  stale: boolean,
  batch: CarryBatch,
  onBlocked?: OnBlocked,
): Promise<boolean> {
  const probe = await openExisting(idb, from, undefined, onBlocked);
  if (!probe) return false;
  const version = probe.version;
  probe.close();
  if (stale) await settle(idb.deleteDatabase(to), to, onBlocked);
  // Opening one version up closes other tabs' connections (Dexie closes on versionchange) and
  // keeps an older bundle from reopening the database, so nothing writes to it during the copy.
  const fence = await openExisting(idb, from, version + 1, onBlocked);
  if (!fence) return false;
  try {
    const stores = storeShapes(fence);
    const req = idb.open(to, version);
    req.onupgradeneeded = () => {
      for (const s of stores) {
        const store = req.result.createObjectStore(s.name, {
          keyPath: s.keyPath,
          autoIncrement: s.autoIncrement,
        });
        for (const i of s.indexes)
          store.createIndex(i.name, i.keyPath, { unique: i.unique, multiEntry: i.multiEntry });
      }
    };
    const target = await settle(req, to, onBlocked);
    try {
      for (const s of stores) await copyStore(fence, target, s, batch);
    } finally {
      target.close();
    }
  } finally {
    fence.close();
  }
  return true;
}

// Opening a database that does not exist would create it, empty: abort that upgrade instead,
// which leaves nothing behind, and answer null.
function openExisting(
  idb: IDBFactory,
  name: string,
  version: number | undefined,
  onBlocked?: OnBlocked,
): Promise<IDBDatabase | null> {
  const req = version === undefined ? idb.open(name) : idb.open(name, version);
  let missing = false;
  req.onupgradeneeded = (e) => {
    if (e.oldVersion !== 0) return;
    missing = true;
    req.transaction?.abort();
  };
  return settle(req, name, onBlocked).catch((err: unknown) => {
    if (missing) return null;
    throw err;
  });
}

function storeShapes(db: IDBDatabase): StoreShape[] {
  const names = [...db.objectStoreNames];
  if (names.length === 0) return [];
  const tx = db.transaction(names, 'readonly');
  return names.map((name) => {
    const s = tx.objectStore(name);
    return {
      name,
      keyPath: s.keyPath,
      autoIncrement: s.autoIncrement,
      indexes: [...s.indexNames].map((n) => {
        const i = s.index(n);
        return { name: n, keyPath: i.keyPath, unique: i.unique, multiEntry: i.multiEntry };
      }),
    };
  });
}

// One readonly transaction reads a batch after the last key copied, one readwrite transaction
// writes it, so at most one batch of photo bytes is in memory at a time.
async function copyStore(from: IDBDatabase, to: IDBDatabase, s: StoreShape, batch: CarryBatch) {
  let after: IDBValidKey | undefined;
  for (;;) {
    const records = await readBatch(from, s.name, after, batch);
    if (records.length === 0) return;
    await writeBatch(to, s, records);
    after = records[records.length - 1].key;
  }
}

function readBatch(
  db: IDBDatabase,
  store: string,
  after: IDBValidKey | undefined,
  batch: CarryBatch,
): Promise<{ key: IDBValidKey; value: unknown }[]> {
  const tx = db.transaction(store, 'readonly');
  const range = after === undefined ? undefined : IDBKeyRange.lowerBound(after, true);
  const records: { key: IDBValidKey; value: unknown }[] = [];
  let bytes = 0;
  const req = tx.objectStore(store).openCursor(range);
  req.onsuccess = () => {
    const cursor = req.result;
    if (!cursor) return;
    records.push({ key: cursor.primaryKey, value: cursor.value });
    bytes += approxBytes(cursor.value);
    if (records.length < batch.records && bytes < batch.bytes) cursor.continue();
  };
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(records);
    tx.onabort = () => reject(tx.error);
  });
}

function writeBatch(
  db: IDBDatabase,
  s: StoreShape,
  records: { key: IDBValidKey; value: unknown }[],
) {
  const tx = db.transaction(s.name, 'readwrite');
  const store = tx.objectStore(s.name);
  for (const { key, value } of records) {
    const carried = carriedValue(s.name, value);
    if (s.keyPath === null) store.put(carried, key);
    else store.put(carried);
  }
  return new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error);
  });
}

function approxBytes(value: unknown): number {
  if (value instanceof ArrayBuffer || ArrayBuffer.isView(value)) return value.byteLength;
  if (typeof Blob !== 'undefined' && value instanceof Blob) return value.size;
  if (typeof value === 'string') return value.length * 2;
  if (Array.isArray(value)) return value.reduce((n: number, v) => n + approxBytes(v), 8);
  if (value !== null && typeof value === 'object')
    return Object.values(value).reduce((n: number, v) => n + approxBytes(v), 8);
  return 8;
}

// rowboat records an unfinished anonymous claim under `adopting` in the account store's meta,
// naming the anonymous store, which is carried under a new name.
function carriedValue(store: string, value: unknown): unknown {
  if (store !== '_rowboat_meta') return value;
  const record = value as { key?: unknown; value?: { anonName?: unknown } };
  if (record.key !== 'adopting' || record.value?.anonName !== storeName(CARRIED_APP, ANON_IDENTITY))
    return value;
  return { ...record, value: { ...record.value, anonName: storeName(APP_STORE, ANON_IDENTITY) } };
}

async function openCarryDb(idb: IDBFactory): Promise<IDBDatabase> {
  const req = idb.open(CARRY_DB, 1);
  req.onupgradeneeded = () => req.result.createObjectStore(CARRY_STORE);
  return settle(req, CARRY_DB);
}

async function carried(idb: IDBFactory): Promise<Set<string>> {
  const db = await openCarryDb(idb);
  try {
    const keys = await settle(
      db.transaction(CARRY_STORE).objectStore(CARRY_STORE).getAllKeys(),
      CARRY_DB,
    );
    return new Set(keys.map(String));
  } finally {
    db.close();
  }
}

async function markCarried(idb: IDBFactory, name: string): Promise<void> {
  const db = await openCarryDb(idb);
  try {
    const tx = db.transaction(CARRY_STORE, 'readwrite');
    tx.objectStore(CARRY_STORE).put(Date.now(), name);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

// A blocked request is not an error: it completes once the other tab closes its connection.
function settle<T>(req: IDBRequest<T>, name: string, onBlocked?: OnBlocked) {
  return new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    // Only open and delete requests fire `blocked`; on any other request the handler never runs.
    (req as IDBRequest<T> & Pick<IDBOpenDBRequest, 'onblocked'>).onblocked = () =>
      onBlocked?.(name);
  });
}
